import { AlertColor, useTheme } from "@mui/material";
import DOMPurify from "dompurify";
import linkify from "linkify-html";
import { FC, PropsWithChildren, useMemo } from "react";
import * as Styled from "./FormErrorMessage.styled";
import { Check } from "./Icons/Check";
import { ErrorIcon } from "./Icons/ErrorIcon";
import { Info } from "./Icons/Info";
import { BodyTextM } from "./Typography";

export type FormErrorMessageProps = PropsWithChildren<{
    name?: string;
    severity?: AlertColor;
}>;

export const FormErrorMessage: FC<FormErrorMessageProps> = ({ name, severity = "error", children }) => {
    const theme = useTheme();

    let icon = undefined;
    const inconSx = {
        marginRight: theme.spacing(3),
    };

    switch (severity) {
        case "info":
            icon = <Info color="info" fontSize="inherit" sx={inconSx} />;
            break;
        case "warning":
            icon = <Info color="warning" fontSize="inherit" sx={inconSx} />;
            break;
        case "error":
            icon = <ErrorIcon color="error" fontSize="inherit" sx={inconSx} />;
            break;
        case "success":
            icon = <Check color="success" fontSize="inherit" sx={inconSx} />;
            break;
    }

    const bodyContent = useMemo(() => {
        if (typeof children === "string") {
            const linkified = linkify(children);
            const sanitized = DOMPurify.sanitize(linkified, {
                ALLOWED_TAGS: ["a", "strong"],
                ALLOWED_ATTR: ["href", "target", "rel"],
            });
            return (
                <BodyTextM
                    dangerouslySetInnerHTML={{
                        __html: sanitized,
                    }}
                />
            );
        }
        return <BodyTextM>{children}</BodyTextM>;
    }, [children]);

    return (
        <Styled.CustomAlert
            data-error-name={name}
            data-error-severity={severity}
            id={name ? `error-${name}` : undefined}
            severity={severity}
            icon={icon}
        >
            {bodyContent}
        </Styled.CustomAlert>
    );
};
